import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaShieldAlt, FaUndoAlt, FaFileContract, FaGavel, FaEnvelope } from "react-icons/fa";
import { motion } from "framer-motion";

const policyMeta = {
  privacy:  { icon: FaShieldAlt,   color: "from-blue-500 to-cyan-500",   bg: "bg-blue-500/10",   border: "border-blue-500/20" },
  refund:   { icon: FaUndoAlt,     color: "from-green-500 to-emerald-500", bg: "bg-green-500/10",  border: "border-green-500/20" },
  terms:    { icon: FaFileContract, color: "from-purple-500 to-violet-500", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  legal:    { icon: FaGavel,       color: "from-orange-500 to-amber-500",  bg: "bg-orange-500/10", border: "border-orange-500/20" },
};

const policies = {
  privacy: {
    title: "Privacy Policy",
    lastUpdated: "January 2025",
    sections: [
      {
        heading: "Information We Collect",
        content: "We collect information you provide directly to us, such as your name, email address, phone number, and any other information you choose to provide when you contact us, request a quote, or use our services.",
      },
      {
        heading: "How We Use Your Information",
        content: "We use the information we collect to provide, maintain, and improve our services, respond to your inquiries, send you technical notices and support messages, and communicate with you about products, services, and events.",
      },
      {
        heading: "Information Sharing",
        content: "We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties. This does not include trusted third parties who assist us in operating our website or servicing you, as long as those parties agree to keep this information confidential.",
      },
      {
        heading: "Data Security",
        content: "We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.",
      },
      {
        heading: "Cookies",
        content: "Our website may use cookies to enhance your experience. You can choose to set your web browser to refuse cookies, or to alert you when cookies are being sent. If you do so, note that some parts of the site may not function properly.",
      },
      {
        heading: "Third-Party Links",
        content: "Our website may contain links to third-party websites. We have no control over the content and practices of those sites and cannot accept responsibility for their respective privacy policies.",
      },
      {
        heading: "Changes to This Policy",
        content: "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page with an updated date.",
      },
      {
        heading: "Contact Us",
        content: "If you have any questions about this Privacy Policy, please contact us at onexvorabusinesssolutions@gmail.com, call us at +91 97180 94931, or visit us at D-184, Sector-63, Noida, UP - 201301, India.",
      },
    ],
  },
  refund: {
    title: "Refund Policy",
    lastUpdated: "January 2025",
    sections: [
      {
        heading: "Overview",
        content: "At ONexvora Business Solutions, we are committed to delivering high-quality services. We understand that circumstances may change, and we have established this refund policy to address such situations fairly.",
      },
      {
        heading: "Eligibility for Refund",
        content: "Refund requests are considered under the following conditions: the project has not been started yet, a written request is submitted within 48 hours of payment, or we are unable to deliver the agreed-upon service due to our own limitations.",
      },
      {
        heading: "Non-Refundable Situations",
        content: "Refunds will not be issued in the following cases: work has already been initiated or delivered, the client changes their mind after work has begun, delays caused by the client's failure to provide required materials or feedback, or for domain registration and hosting services once activated.",
      },
      {
        heading: "Partial Refunds",
        content: "In cases where work has been partially completed, a partial refund may be considered based on the percentage of work remaining. This will be evaluated on a case-by-case basis by our management team.",
      },
      {
        heading: "Refund Process",
        content: "To request a refund, contact us at onexvorabusinesssolutions@gmail.com with your order details and reason for the request. Approved refunds will be processed within 7–10 business days to the original payment method.",
      },
      {
        heading: "Monthly Services",
        content: "For monthly retainer services (Digital Marketing, Social Media Management), cancellations must be made 15 days before the next billing cycle. No refunds are issued for the current active month.",
      },
      {
        heading: "Contact for Disputes",
        content: "If you are unsatisfied with our services, please reach out to us first at onexvorabusinesssolutions@gmail.com, call +91 97180 94931, or visit D-184, Sector-63, Noida, UP - 201301, India. We are committed to resolving issues amicably before any formal dispute process.",
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    lastUpdated: "January 2025",
    sections: [
      {
        heading: "Acceptance of Terms",
        content: "By accessing and using ONexvora Business Solutions' services, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.",
      },
      {
        heading: "Services",
        content: "ONexvora provides web development, mobile app development, digital marketing, business strategy consulting, domain & hosting, and social media management services. All services are subject to availability and our acceptance of your project.",
      },
      {
        heading: "Client Responsibilities",
        content: "Clients are responsible for providing accurate project requirements, timely feedback, necessary content and assets, and timely payments as per the agreed schedule. Delays caused by the client may affect project timelines.",
      },
      {
        heading: "Intellectual Property",
        content: "Upon full payment, the client receives ownership of the final deliverables. ONexvora retains the right to showcase completed work in our portfolio unless a non-disclosure agreement is signed. All third-party assets used remain subject to their respective licenses.",
      },
      {
        heading: "Payment Terms",
        content: "Projects typically require a 50% advance payment before work begins, with the remaining balance due upon project completion. For monthly services, payment is due at the beginning of each billing cycle.",
      },
      {
        heading: "Limitation of Liability",
        content: "ONexvora shall not be liable for any indirect, incidental, or consequential damages arising from the use of our services. Our total liability shall not exceed the amount paid by the client for the specific service in question.",
      },
      {
        heading: "Termination",
        content: "Either party may terminate the agreement with 15 days written notice. Upon termination, the client is responsible for payment of all work completed up to the termination date.",
      },
      {
        heading: "Governing Law",
        content: "These Terms and Conditions are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in India.",
      },
      {
        heading: "Contact Us",
        content: "For any questions regarding these Terms, contact us at onexvorabusinesssolutions@gmail.com, call +91 97180 94931, or visit D-184, Sector-63, Noida, UP - 201301, India.",
      },
    ],
  },
  legal: {
    title: "Legal Policy",
    lastUpdated: "January 2025",
    sections: [
      {
        heading: "Company Information",
        content: "ONexvora Business Solutions is a registered business entity operating in India. We provide technology and business consulting services to clients across India and internationally.",
      },
      {
        heading: "Disclaimer",
        content: "The information provided on this website is for general informational purposes only. While we strive to keep the information accurate and up to date, we make no representations or warranties of any kind about the completeness, accuracy, or reliability of the information.",
      },
      {
        heading: "Copyright Notice",
        content: "All content on this website, including text, graphics, logos, images, and software, is the property of ONexvora Business Solutions and is protected by applicable Indian and international copyright laws.",
      },
      {
        heading: "Trademark",
        content: "ONexvora and the ONexvora logo are trademarks of ONexvora Business Solutions. You may not use these trademarks without our prior written permission.",
      },
      {
        heading: "Prohibited Activities",
        content: "Users are prohibited from using our website or services for any unlawful purpose, transmitting harmful or malicious content, attempting to gain unauthorized access to our systems, or engaging in any activity that disrupts our services.",
      },
      {
        heading: "Dispute Resolution",
        content: "In the event of any dispute arising out of or in connection with our services, the parties shall first attempt to resolve the dispute through good-faith negotiation. If unresolved, disputes shall be referred to arbitration under Indian law.",
      },
      {
        heading: "Compliance",
        content: "ONexvora Business Solutions complies with applicable Indian laws including the Information Technology Act, 2000, and the Information Technology (Amendment) Act, 2008, regarding data protection and electronic commerce.",
      },
      {
        heading: "Contact",
        content: "For any legal inquiries, contact us at onexvorabusinesssolutions@gmail.com, call +91 97180 94931, or visit D-184, Sector-63, Noida, UP - 201301, India. We aim to respond to all legal queries within 3–5 business days.",
      },
    ],
  },
};

export default function PolicyPage({ type }) {
  const navigate = useNavigate();
  const policy = policies[type];
  const meta = policyMeta[type];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  if (!policy) return null;

  const Icon = meta.icon;

  return (
    <div className="bg-dark min-h-screen">
      {/* Hero Header */}
      <div className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 gradient-bg opacity-10" />
        <div className={`absolute top-0 right-0 w-[500px] h-[500px] ${meta.bg} rounded-full blur-3xl`} />
        <div className={`absolute bottom-0 left-0 w-72 h-72 ${meta.bg} rounded-full blur-3xl`} />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-all mb-10 text-sm group"
          >
            <span className="w-7 h-7 rounded-full border border-gray-600 group-hover:border-white flex items-center justify-center transition-all">
              <FaArrowLeft size={10} />
            </span>
            Back to Home
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-5"
          >
            <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br ${meta.color} flex items-center justify-center shadow-2xl flex-shrink-0`}>
              <Icon size={28} className="text-white" />
            </div>
            <div>
              <p className={`text-xs font-semibold uppercase tracking-widest bg-gradient-to-r ${meta.color} bg-clip-text text-transparent mb-1`}>Onexvora Business Solutions</p>
              <h1 className="text-3xl sm:text-5xl font-black text-white">{policy.title}</h1>
              <p className="text-gray-500 text-sm mt-1">Last updated: {policy.lastUpdated}</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-20">
        <div className="space-y-4">
          {policy.sections.map((section, i) => (
            <motion.div
              key={section.heading}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className={`glass rounded-2xl p-5 sm:p-7 border ${meta.border} hover:border-opacity-60 transition-all`}
            >
              <h2 className="text-white font-bold text-base sm:text-lg mb-3 flex items-center gap-3">
                <span className={`w-7 h-7 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center text-white text-xs font-black flex-shrink-0 shadow-lg`}>
                  {i + 1}
                </span>
                {section.heading}
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed pl-10">{section.content}</p>
            </motion.div>
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className={`mt-10 glass border ${meta.border} rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left`}
        >
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center flex-shrink-0`}>
            <FaEnvelope size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold text-sm">Have questions about this policy?</p>
            <p className="text-gray-400 text-xs mt-0.5">We're here to help. Reach out anytime.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <a href="mailto:onexvorabusinesssolutions@gmail.com"
              className={`px-5 py-2.5 rounded-xl bg-gradient-to-r ${meta.color} text-white text-sm font-semibold hover:opacity-90 transition-all shadow-lg whitespace-nowrap`}>
              onexvorabusinesssolutions@gmail.com
            </a>
            <a href="tel:+919718094931"
              className="px-5 py-2.5 rounded-xl glass border border-white/10 text-white text-sm font-semibold hover:opacity-90 transition-all whitespace-nowrap">
              +91 97180 94931
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
